import React from 'react';
import { RiFileListLine as DocumentIcon } from 'react-icons/ri';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LettersList from '@/components/letters/LettersList';
import LetterCreateForm, {
  LetterCreatePayload,
} from '@/components/letters/LetterCreateForm';
import BatchLetterCreateForm from '@/components/letters/BatchLetterCreateForm';
import LetterGenerationProgress from '@/components/letters/LetterGenerationProgress';
import BatchLetterProgress from '@/components/letters/BatchLetterProgress';
import LetterViewer from '@/components/letters/LetterViewer';
import { LettersApi } from '@/api';
import type { LetterRecord, BatchLetterConfig } from '@/types/letters';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import type { FileWithType } from '@/components/letters/MultiFileUpload';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

type Mode =
  | 'list'
  | 'create'
  | 'batch_create'
  | 'generating'
  | 'batch_generating'
  | 'view';

const LettersTab: React.FC<{ caseId: string; visaType?: string }> = ({ caseId, visaType }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState<Mode>('list');
  const [letters, setLetters] = useState<LetterRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [creating, setCreating] = useState<boolean>(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [selected, setSelected] = useState<LetterRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get letter ID from URL params for persistence
  const letterIdFromUrl = searchParams.get('letterId');

  const loadLetters = async (): Promise<LetterRecord[]> => {
    try {
      setLoading(true);
      const data = await LettersApi.listLetters(caseId);
      // Sort by most recently updated first
      const sorted = [...data].sort((a, b) => {
        const au = new Date(a.updated_at || a.created_at || 0).getTime();
        const bu = new Date(b.updated_at || b.created_at || 0).getTime();
        return bu - au;
      });
      setLetters(sorted);
      return sorted;
    } catch (e) {
      console.error('Failed to load letters:', e);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      loadLetters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // Restore letter selection from URL params after letters are loaded
  useEffect(() => {
    if (letters.length > 0 && letterIdFromUrl && !selected) {
      const letter = letters.find(l => l.id === letterIdFromUrl);
      if (letter) {
        setSelected(letter);
        setMode('view');
      }
    }
  }, [letters, letterIdFromUrl]);

  const handleCreateSubmit = async (payload: LetterCreatePayload) => {
    try {
      setCreating(true);
      setError(null);

      // Upload all files first and collect document IDs
      const uploadedDocumentIds: string[] = [];
      const failedUploads: string[] = [];

      if (payload.files && payload.files.length > 0) {
        console.log(
          `Uploading ${payload.files.length} documents for letter generation...`
        );

        for (const fileWithType of payload.files) {
          try {
            const uploadResult = await LettersApi.uploadRefereeCVForCase(
              caseId,
              fileWithType.file
            );

            if (uploadResult.success && uploadResult.documentId) {
              uploadedDocumentIds.push(uploadResult.documentId);
              console.log(
                `Uploaded ${fileWithType.documentType}: ${uploadResult.fileName} (ID: ${uploadResult.documentId})`
              );
            } else {
              failedUploads.push(fileWithType.file.name);
            }
          } catch (uploadError) {
            console.error(
              `Failed to upload ${fileWithType.file.name}:`,
              uploadError
            );
            failedUploads.push(fileWithType.file.name);
          }
        }

        console.log(
          `Successfully uploaded ${uploadedDocumentIds.length} of ${payload.files.length} documents`
        );

        // Warn user if some uploads failed but continue
        if (failedUploads.length > 0 && uploadedDocumentIds.length > 0) {
          console.warn(`Some files failed to upload: ${failedUploads.join(', ')}`);
        }

        // If all required uploads failed, show error and abort
        if (failedUploads.length > 0 && uploadedDocumentIds.length === 0 && payload.files.length > 0) {
          setError(`Failed to upload files: ${failedUploads.join(', ')}. Please try again.`);
          return;
        }
      }

      const task = await LettersApi.createLetter({
        caseId,
        letter_type: payload.letter_type,
        referee_name: payload.referee_name,
        recommender_background: payload.recommender_background,
        recommender_document_id: payload.recommender_document_id,
        relationship_to_beneficiary: payload.relationship_to_beneficiary,
        achievements_to_highlight: payload.achievements_to_highlight,
        selected_criteria: payload.selected_criteria,
        selected_sections: payload.selected_sections,
        whitelist: payload.whitelist,
        blacklist: payload.blacklist,
        file_ids:
          uploadedDocumentIds.length > 0 ? uploadedDocumentIds : undefined,
        target_word_count: payload.target_word_count,
        recommender_is_us_based: payload.recommender_is_us_based,
        recommender_country: payload.recommender_country,
        metadata: payload.metadata,
      });

      // Track generation start
      analytics.track(ANALYTICS_EVENTS.LETTER_GENERATION_STARTED, {
        case_id: caseId,
        letter_type: payload.letter_type,
        referee_name: payload.referee_name,
        criteria_count: payload.selected_criteria?.length || 0,
        sections_count: payload.selected_sections?.length || 0,
        files_count: uploadedDocumentIds.length,
        is_batch: false,
      });

      const id = task.taskId || task.task_id;
      if (id) {
        setTaskId(id);
        setMode('generating');
      } else {
        console.warn('No taskId returned from create letter');
        setError('Letter generation failed to start. Please try again.');
      }
    } catch (e: any) {
      console.error('Create letter failed:', e);
      const errorMessage = e?.message || e?.response?.data?.message || 'An unexpected error occurred';
      setError(`Failed to generate letter: ${errorMessage}`);
    } finally {
      setCreating(false);
    }
  };

  const handleOpenLetter = async (letter: LetterRecord) => {
    if (!letter.id) {
      console.error('Cannot open letter: missing letter ID', letter);
      alert(
        'Cannot open this letter: Letter ID is missing. Please try refreshing the page.'
      );
      return;
    }

    // Track letter view
    analytics.track(ANALYTICS_EVENTS.LETTER_VIEWED, {
      case_id: caseId,
      letter_id: letter.id,
      letter_type: letter.letter_type,
    });

    setSelected(letter);
    setMode('view');
    // Update URL to persist selection
    setSearchParams({ letterId: letter.id });
  };

  const handleBatchCreate = async (
    letterConfigs: Array<BatchLetterConfig & { files?: FileWithType[] }>,
    commonSettings: {
      common_instructions?: string;
      common_whitelist?: string;
      common_blacklist?: string;
      common_files?: FileWithType[];
    }
  ) => {
    try {
      setCreating(true);

      // Upload common files first
      const commonFileIds: string[] = [];
      if (
        commonSettings.common_files &&
        commonSettings.common_files.length > 0
      ) {
        console.log(
          `Uploading ${commonSettings.common_files.length} common documents...`
        );

        for (const fileWithType of commonSettings.common_files) {
          try {
            const uploadResult = await LettersApi.uploadRefereeCVForCase(
              caseId,
              fileWithType.file
            );

            if (uploadResult.success && uploadResult.documentId) {
              commonFileIds.push(uploadResult.documentId);
              console.log(
                `Uploaded common ${fileWithType.documentType}: ${uploadResult.fileName} (ID: ${uploadResult.documentId})`
              );
            }
          } catch (uploadError) {
            console.error(
              `Failed to upload common file ${fileWithType.file.name}:`,
              uploadError
            );
          }
        }

        console.log(
          `Successfully uploaded ${commonFileIds.length} of ${commonSettings.common_files.length} common documents`
        );
      }

      // Upload individual letter files and prepare letter configs
      const processedLetterConfigs: BatchLetterConfig[] = [];

      for (const letterConfig of letterConfigs) {
        const letterFileIds: string[] = [];

        if (letterConfig.files && letterConfig.files.length > 0) {
          console.log(
            `Uploading ${letterConfig.files.length} documents for ${letterConfig.referee_name}...`
          );

          for (const fileWithType of letterConfig.files) {
            try {
              const uploadResult = await LettersApi.uploadRefereeCVForCase(
                caseId,
                fileWithType.file
              );

              if (uploadResult.success && uploadResult.documentId) {
                letterFileIds.push(uploadResult.documentId);
                console.log(
                  `Uploaded ${fileWithType.documentType} for ${letterConfig.referee_name}: ${uploadResult.fileName}`
                );
              }
            } catch (uploadError) {
              console.error(
                `Failed to upload file for ${letterConfig.referee_name}: ${fileWithType.file.name}`,
                uploadError
              );
            }
          }
        }

        // Create processed config without the files property
        const { files, ...configWithoutFiles } = letterConfig;
        processedLetterConfigs.push({
          ...configWithoutFiles,
          file_ids: letterFileIds.length > 0 ? letterFileIds : undefined,
        });
      }

      const batch = await LettersApi.createBatchLetters({
        project_id: caseId,
        letters: processedLetterConfigs,
        common_file_ids: commonFileIds.length > 0 ? commonFileIds : undefined,
        common_instructions: commonSettings.common_instructions,
        common_whitelist: commonSettings.common_whitelist,
        common_blacklist: commonSettings.common_blacklist,
      });

      // Track batch generation start
      analytics.track(ANALYTICS_EVENTS.LETTER_GENERATION_STARTED, {
        case_id: caseId,
        letter_count: processedLetterConfigs.length,
        common_files_count: commonFileIds.length,
        is_batch: true,
      });

      setBatchId(batch.batch_id);
      setMode('batch_generating');
    } catch (e) {
      console.error('Batch create failed:', e);
    } finally {
      setCreating(false);
    }
  };

  const handleBatchComplete = async () => {
    try {
      // Reload letters list
      await loadLetters();
      setMode('list');
    } catch (e) {
      console.error('Post-batch handling failed:', e);
      setMode('list');
    } finally {
      setBatchId(null);
    }
  };

  const handleGenerationFinal = async (letterId?: string) => {
    try {
      if (letterId) {
        const fresh = await LettersApi.getLetter(letterId);
        if (fresh && fresh.id) {
          setSelected(fresh);
          setMode('view');
          await loadLetters();
          return;
        }
      }
      // Fallback: reload list and pick the most recently updated
      const latest = await loadLetters();
      if (latest.length > 0) {
        // Find the first letter with a valid id
        const validLetter = latest.find(l => l.id);
        if (validLetter) {
          setSelected(validLetter);
          setMode('view');
        } else {
          console.error('No letters with valid IDs found');
          setMode('list');
        }
      } else {
        setMode('list');
      }
    } catch (e) {
      console.error('Post-generation handling failed:', e);
      setMode('list');
    } finally {
      setTaskId(null);
    }
  };

  if (loading && mode === 'list') {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (mode === 'list') {
    return (
      <LettersList
        letters={letters}
        loading={loading}
        onCreate={() => {
          setMode('create');
          analytics.track(ANALYTICS_EVENTS.LETTER_CREATE_FORM_OPENED, {
            case_id: caseId,
          });
        }}
        onOpen={handleOpenLetter}
        onDelete={async letter => {
          try {
            await LettersApi.deleteLetter(letter.id);

            analytics.track(ANALYTICS_EVENTS.LETTER_DELETED, {
              case_id: caseId,
              letter_id: letter.id,
              letter_type: letter.letter_type,
            });

            await loadLetters();
          } catch (e) {
            console.error('Delete letter failed:', e);
          }
        }}
      />
    );
  }

  if (mode === 'create') {
    return (
      <LetterCreateForm
        onCancel={() => {
          setMode('list');
          setError(null);
          loadLetters();
        }}
        onCreate={handleCreateSubmit}
        isSubmitting={creating}
        caseId={caseId}
        visaType={visaType}
        error={error}
        onClearError={() => setError(null)}
      />
    );
  }

  if (mode === 'batch_create') {
    return (
      <BatchLetterCreateForm
        onCancel={() => {
          setMode('list');
          loadLetters();
        }}
        onCreate={handleBatchCreate}
        isSubmitting={creating}
      />
    );
  }

  if (mode === 'generating' && taskId) {
    return (
      <LetterGenerationProgress
        taskId={taskId}
        onBack={() => {
          setMode('list');
          loadLetters();
        }}
        onFinal={handleGenerationFinal}
      />
    );
  }

  if (mode === 'batch_generating' && batchId) {
    return (
      <BatchLetterProgress
        batchId={batchId}
        onBack={() => {
          setMode('list');
          loadLetters();
        }}
        onComplete={handleBatchComplete}
      />
    );
  }

  if (mode === 'view' && selected) {
    return (
      <LetterViewer
        letter={selected}
        caseId={caseId}
        onBack={() => {
          setSelected(null);
          setMode('list');
          setSearchParams({}); // Clear URL params
          loadLetters();
        }}
      />
    );
  }

  // Fallback
  return (
    <div className="text-center py-20">
      <DocumentIcon className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
      <h3 className="text-xl font-semibold text-neutral-900 mb-2">
        No content
      </h3>
      <p className="text-neutral-500 text-sm max-w-sm mx-auto">
        Try reloading the page.
      </p>
    </div>
  );
};

export default LettersTab;
